import React, { useEffect, useState, useRef } from 'react';
import { Search, Filter, BookOpen, ArrowLeft, Bookmark, Heart, Eye, Send, ChevronDown, MessageSquare, PenLine, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ArchiveQuestionDetail } from '../components/ArchiveQuestionDetail';
import { ArchivedForumQuestionDetail } from '../components/ArchivedForumQuestionDetail';
import { ShareQuestionModal } from '../components/ShareQuestionModal';
import { NoteModal } from '../components/NoteModal';
import { subjectColors, getSubjectGradient, DERS_RENKLERI } from '../utils/constants';
import { useToast } from '../components/Toast';
// Get category color helper function
const getCategoryColor = (subject: string) => {
  switch (subject) {
    case 'Matematik':
      return 'bg-blue-400/10 text-blue-400';
    case 'Türkçe':
      return 'bg-red-400/10 text-red-400';
    case 'Fen Bilimleri':
      return 'bg-green-400/10 text-green-400';
    case 'T.C. İnkılap Tarihi':
      return 'bg-amber-400/10 text-amber-400';
    case 'İngilizce':
      return 'bg-indigo-400/10 text-indigo-400';
    case 'Din Kültürü':
      return 'bg-teal-400/10 text-teal-400';
    default:
      return 'bg-purple-400/10 text-purple-400';
  }
};
interface Question {
  id: number;
  subject: string;
  topic: string;
  subtopic: string;
  difficulty: string;
  solved: boolean;
  date: string;
  archived: boolean;
  title?: string;
  content?: string;
  options?: string[];
  correctOption?: number;
  image?: string;
  notes?: string;
  likes?: string[];
  views?: number;
}
// Fen Bilimleri alt konuları için yapı
interface SubTopic {
  title: string;
  topics: string[];
}
const subjects = [{
  id: 'turkce',
  title: 'Türkçe',
  topics: ['Sözcükte Anlam', 'Deyimler ve Atasözleri', 'Cümlede Anlam', 'Sözel Mantık / Görsel Okuma', 'Parçada Anlam', 'Söz Sanatları', 'Metin Türleri', 'Noktalama İşaretleri', 'Yazım Kuralları', 'Fiilimsiler', 'Cümlenin Öğeleri', 'Cümle Türleri', 'Anlatım Bozukluğu'],
  image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/files/53920870-2e72-4897-9a68-0d2901d601e4/image_fx__(5).jpg'
}, {
  id: 'matematik',
  title: 'Matematik',
  topics: ['Çarpanlar ve Katlar', 'Üslü Sayılar', 'Köklü Sayılar', 'Veri Analizi', 'Olasılık', 'Cebirsel İfadeler', 'Doğrusal Denklemler ve Eğim', 'Eşitsizlikler', 'Üçgenler', 'Eşlik ve Benzerlik', 'Dönüşüm Geometrisi', 'Geometrik Cisimler'],
  image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/files/472f13e1-d5a1-4908-a2f4-ca37914378a1/image_fx__(6).jpg'
}, {
  id: 'fen',
  title: 'Fen Bilimleri',
  topics: [],
  subTopics: [{
    title: 'Mevsimler ve İklim',
    topics: ['Mevsimlerin Oluşumu', 'İklim ve Hava Hareketleri']
  }, {
    title: 'DNA ve Genetik Kod',
    topics: ['DNA ve Genetik Kod', 'Kalıtım', 'Mutasyon ve Modifikasyon', 'Adaptasyon', 'Biyoteknoloji']
  }, {
    title: 'Basınç',
    topics: ['Basınç']
  }, {
    title: 'Madde ve Endüstri',
    topics: ['Periyodik Sistem', 'Fiziksel ve Kimyasal Değişimler', 'Kimyasal Tepkimeler', 'Asitler ve Bazlar', 'Maddenin Isı ile Etkileşimi', "Türkiye'de Kimya Endüstrisi"]
  }, {
    title: 'Basit Makineler',
    topics: ['Basit Makineler']
  }, {
    title: 'Enerji Dönüşümleri ve Çevre Bilimi',
    topics: ['Besin Zinciri ve Enerji Akışı', 'Enerji Dönüşümleri', 'Madde Döngüleri ve Çevre Sorunları', 'Sürdürülebilir Kalkınma']
  }, {
    title: 'Elektrik Yükleri ve Elektrik Enerjisi',
    topics: ['Elektrik Yükleri ve Elektriklenme', 'Elektrik Yüklü Cisimler', 'Elektrik Enerjisinin Dönüşümü']
  }],
  image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/files/342defcc-7c1d-494c-b44e-30031e1df94e/image_fx__(7).jpg'
}, {
  id: 'inkılap',
  title: 'T.C. İnkılap Tarihi ve Atatürkçülük',
  topics: ['Bir Kahraman Doğuyor', 'Milli Uyanış: Bağımsızlık Yolunda Atılan Adımlar', 'Milli Bir Destan: Ya İstiklal, Ya Ölüm', 'Atatürkçülük ve Çağdaşlaşan Türkiye', 'Demokratikleşme Çabaları', 'Atatürk Dönemi Dış Politika', "Atatürk'ün Ölümü ve Sonrası"],
  image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/files/8f55533b-a8d1-414d-95ae-dd4d9212be55/image_fx__(8).jpg'
}, {
  id: 'ingilizce',
  title: 'İngilizce',
  topics: ['Friendship', 'Teen Life', 'In The Kitchen', 'On The Phone', 'The Internet', 'Adventures', 'Tourism', 'Chores', 'Science', 'Natural Forces'],
  image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/files/ea67535a-604d-4bf1-9bcd-1b4b904b3521/image_fx__(9).jpg'
}, {
  id: 'din',
  title: 'Din Kültürü ve Ahlâk Bilgisi',
  topics: ['Kader İnancı', 'Bir Peygamber Tanıyorum: Hz. Musa', 'Zekat ve Sadaka', 'Din ve Hayat', "Hz. Muhammed'in Örnekliği", "Kur'an-ı Kerim ve Özellikleri", "İslam'ın Paylaşma ve Yardımlaşmaya Verdiği Önem", 'İslam Dinine Göre Kötü Davranışlar', 'İnsanın Paylaşma ve Yardımlaşma İhtiyacı (Tevekkül)', 'Hac ibadeti'],
  image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/files/a58167ac-e09a-4736-bf01-faa35ee6e6a7/image_fx__(10).jpg'
}];
interface ArchivedForumQuestion {
  id: number;
  title: string;
  content?: string;
  subject: string;
  topic: string;
  image?: string;
  forumDate: string;
  archivedDate: string;
  notes: string;
  noteImages: string[];
  bestAnswer?: string | null;
  teacherApproved?: string | null;
  mostLiked?: string | null;
}
export function QuestionArchive() {
  const navigate = useNavigate();
  // Level 1: Subject selection
  const [selectedSubject, setSelectedSubject] = useState<(typeof subjects)[0] | null>(null);
  // Level 2: Topic selection
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<SubTopic | null>(null);
  // Level 3: Question details
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('everyone');
  const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'mostLiked' | 'mostViewed' | 'titleAZ' | 'titleZA'>('newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [questionToShare, setQuestionToShare] = useState<Question | null>(null);
  const [activeTab, setActiveTab] = useState<'solved' | 'forum'>('solved');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedNoteQuestion, setSelectedNoteQuestion] = useState<ArchivedForumQuestion | Question | null>(null);
  const [selectedForumQuestion, setSelectedForumQuestion] = useState<ArchivedForumQuestion | null>(null);
  const {
    showToast,
    ToastContainer
  } = useToast();
  // Data
  const [questions, setQuestions] = useState<Question[]>(() => {
    const saved = localStorage.getItem('questions');
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    // Add likes and views if they don't exist
    return parsed.map((q: any) => ({
      ...q,
      likes: q.likes || [],
      views: q.views || Math.floor(Math.random() * 50) + 1,
      // For archive purposes, create a subtopic from title if it doesn't exist
      subtopic: q.subtopic || q.title || 'Adsız Soru'
    }));
  });
  const [archivedForumQuestions, setArchivedForumQuestions] = useState<ArchivedForumQuestion[]>(() => {
    const saved = localStorage.getItem('archivedForumQuestions');
    return saved ? JSON.parse(saved) : [];
  });
  // Function to handle notes for forum questions
  const handleSaveForumQuestionNotes = (id: number, notes: string, noteImages: string[] = []) => {
    setArchivedForumQuestions(prev => {
      const updated = prev.map(q => q.id === id ? {
        ...q,
        notes,
        noteImages
      } : q);
      localStorage.setItem('archivedForumQuestions', JSON.stringify(updated));
      return updated;
    });
  };
  // Function to handle toggling archive status
  const handleToggleArchive = (questionId: number) => {
    setQuestions(prev => {
      const updated = prev.map(q => q.id === questionId ? {
        ...q,
        archived: !q.archived
      } : q);
      localStorage.setItem('questions', JSON.stringify(updated));
      return updated;
    });
  };
  // Function to handle saving notes for solved questions
  const handleSaveNotes = (questionId: number, notes: string, noteImages: string[] = []) => {
    setQuestions(prev => {
      const updated = prev.map(q => q.id === questionId ? {
        ...q,
        notes: notes,
        noteImages: noteImages
      } : q);
      localStorage.setItem('questions', JSON.stringify(updated));
      return updated;
    });
  };
  // Function to handle liking a question
  const handleLike = (questionId: number) => {
    const currentUserId = 'currentUser';
    setQuestions(prev => {
      const updated = prev.map(q => {
        if (q.id === questionId) {
          const likes = q.likes?.includes(currentUserId) ? q.likes?.filter(id => id !== currentUserId) || [] : [...(q.likes || []), currentUserId];
          return {
            ...q,
            likes
          };
        }
        return q;
      });
      localStorage.setItem('questions', JSON.stringify(updated));
      return updated;
    });
  };
  // Function to handle viewing a question
  const handleView = (questionId: number) => {
    setQuestions(prev => {
      const updated = prev.map(q => q.id === questionId ? {
        ...q,
        views: (q.views || 0) + 1
      } : q);
      localStorage.setItem('questions', JSON.stringify(updated));
      return updated;
    });
  };
  // Function to handle sharing a question
  const handleShare = (questionId: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      setQuestionToShare(question);
      setShowShareModal(true);
    }
  };
  // Function to handle submitting a share
  const handleShareSubmit = (data: {
    recipientId: string;
    note: string;
  }) => {
    // In a real app, this would send the share data to the server
    console.log(`Shared question ${questionToShare?.id} with ${data.recipientId}. Note: ${data.note}`);
    setShowShareModal(false);
    setQuestionToShare(null);
  };
  // Function to handle going back from a unit
  const handleBackFromUnit = () => {
    setSelectedUnit(null);
    setSelectedTopic(null);
  };
  // Function to sort questions based on sort option
  const sortQuestions = (questions: Question[]) => {
    switch (sortOption) {
      case 'newest':
        return [...questions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      case 'oldest':
        return [...questions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      case 'mostLiked':
        return [...questions].sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0));
      case 'mostViewed':
        return [...questions].sort((a, b) => (b.views || 0) - (a.views || 0));
      case 'titleAZ':
        return [...questions].sort((a, b) => (a.title || a.subtopic).localeCompare(b.title || b.subtopic, 'tr'));
      case 'titleZA':
        return [...questions].sort((a, b) => (b.title || b.subtopic).localeCompare(a.title || a.subtopic, 'tr'));
      default:
        return questions;
    }
  };
  // Function to sort forum questions based on sort option
  const sortForumQuestions = (questions: ArchivedForumQuestion[]) => {
    switch (sortOption) {
      case 'newest':
        return [...questions].sort((a, b) => new Date(b.archivedDate).getTime() - new Date(a.archivedDate).getTime());
      case 'oldest':
        return [...questions].sort((a, b) => new Date(a.archivedDate).getTime() - new Date(b.archivedDate).getTime());
      default:
        return questions;
    }
  };
  // Function to handle opening the note modal
  const handleOpenNoteModal = (question: ArchivedForumQuestion | Question) => {
    setSelectedNoteQuestion(question);
    setShowNoteModal(true);
  };
  // Function to handle closing the note modal
  const handleCloseNoteModal = () => {
    setShowNoteModal(false);
    setSelectedNoteQuestion(null);
  };
  // Function to handle saving notes from the modal
  const handleSaveNotesFromModal = (id: number, notes: string, noteImages: string[]) => {
    if ('subject' in selectedNoteQuestion! && 'topic' in selectedNoteQuestion! && 'archivedDate' in selectedNoteQuestion!) {
      // It's a forum question
      handleSaveForumQuestionNotes(id, notes, noteImages);
    } else {
      // It's a solved question
      handleSaveNotes(id, notes, noteImages);
    }
  };
  // Function to open a forum question in a new tab
  const handleOpenForumQuestion = (questionId: number) => {
    // Find the question in archived questions
    const question = archivedForumQuestions.find(q => q.id === questionId);
    if (question) {
      // Navigate to the specific forum thread using the question's ID
      navigate(`/forum/konu/${questionId}`);
    }
  };
  // LEVEL 1: Subject Selection (Ders Seçimi) - Common for both tabs
  if (!selectedSubject) {
    return <div className="space-y-6" data-id="element-2846">
        <h1 className="text-2xl font-bold" data-id="element-2847">Soru Arşivim</h1>
        {/* Tabs */}
        <div className="border-b border-gray-800 mb-6" data-id="element-2848">
          <div className="flex" data-id="element-2849">
            <button onClick={() => setActiveTab('solved')} className={`px-6 py-3 font-medium text-sm relative ${activeTab === 'solved' ? 'text-purple-400' : 'text-gray-400 hover:text-gray-300'}`} data-id="element-2850">
              Çözdüğüm Sorular
              {activeTab === 'solved' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-purple-500" data-id="element-2851"></span>}
            </button>
            <button onClick={() => setActiveTab('forum')} className={`px-6 py-3 font-medium text-sm relative ${activeTab === 'forum' ? 'text-purple-400' : 'text-gray-400 hover:text-gray-300'}`} data-id="element-2852">
              Forum Soruları
              {activeTab === 'forum' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-purple-500" data-id="element-2853"></span>}
            </button>
          </div>
        </div>
        {/* Subject grid - common for both tabs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2854">
          {subjects.map(subject => {
          const gradient = getSubjectGradient(subject.id);
          return <div key={subject.id} className={`relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${gradient.from} ${gradient.to} group cursor-pointer`} onClick={() => setSelectedSubject(subject)} data-id="element-2855">
                <div className="relative bg-black/90 rounded-lg h-full" data-id="element-2856">
                  <div className="relative aspect-[16/9]" data-id="element-2857">
                    <img src={subject.image} alt={subject.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-2858" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" data-id="element-2859" />
                    <div className="absolute inset-0 flex items-end justify-center p-8" data-id="element-2860">
                      <h3 className="text-xl font-semibold text-center" data-id="element-2861">
                        {subject.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>;
  }
  // LEVEL 2: Topic Selection (Konu Seçimi) - If a subject is selected but no topic yet
  if (selectedSubject && !selectedTopic) {
    const gradient = getSubjectGradient(selectedSubject.id);
    // Handle special case for Fen Bilimleri which has subTopics
    const topicsToDisplay = selectedSubject.id === 'fen' ? (selectedSubject.subTopics as SubTopic[]).map(st => st.title) : selectedSubject.topics;
    return <div className="space-y-6" data-id="element-2862">
        <div className="flex items-center gap-4" data-id="element-2863">
          <button onClick={() => setSelectedSubject(null)} className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${gradient.from} ${gradient.to} text-white hover:opacity-90 transition-all`} data-id="element-2864">
            <ArrowLeft className="w-5 h-5" data-id="element-2865" />
            Geri
          </button>
          <h1 className="text-2xl font-bold" data-id="element-2866">{selectedSubject.title}</h1>
        </div>
        {/* Topics grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2867">
          {topicsToDisplay.map(topic => <div key={topic} className={`p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${gradient.from} ${gradient.to} cursor-pointer hover:shadow-[0_0_10px_rgba(255,255,255,0.2)] transition-all`} onClick={() => {
          if (selectedSubject.id === 'fen') {
            // For Fen Bilimleri, set the selected unit
            const unit = (selectedSubject.subTopics as SubTopic[]).find(st => st.title === topic);
            if (unit) {
              setSelectedUnit(unit);
              // If there's only one topic in the unit, select it directly
              if (unit.topics.length === 1) {
                setSelectedTopic(unit.topics[0]);
              }
            }
          } else {
            // For other subjects, set the selected topic directly
            setSelectedTopic(topic as string);
          }
        }} data-id="element-2868">
              <div className="p-6 bg-black/90 rounded-lg h-full" data-id="element-2869">
                <h3 className="text-lg font-medium" data-id="element-2870">{topic}</h3>
                {activeTab === 'solved' && <div className="mt-2 text-sm text-gray-400" data-id="element-2871">
                    {selectedSubject.id === 'fen' ? `${(selectedSubject.subTopics as SubTopic[]).find(st => st.title === topic)?.topics.length || 0} alt konu` : 'Arşivlenen sorular'}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>;
  }
  // Handle Fen Bilimleri special case where we need to select a subtopic
  if (selectedSubject?.id === 'fen' && selectedUnit && !selectedTopic) {
    const gradient = getSubjectGradient(selectedSubject.id);
    return <div className="space-y-6" data-id="element-2872">
        <div className="flex items-center gap-4" data-id="element-2873">
          <button onClick={() => setSelectedUnit(null)} className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${gradient.from} ${gradient.to} text-white hover:opacity-90 transition-all`} data-id="element-2874">
            <ArrowLeft className="w-5 h-5" data-id="element-2875" />
            Geri
          </button>
          <h1 className="text-2xl font-bold" data-id="element-2876">
            {selectedSubject.title} - {selectedUnit.title}
          </h1>
        </div>
        {/* Subtopics grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2877">
          {selectedUnit.topics.map(topic => <div key={topic} className={`p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${gradient.from} ${gradient.to} cursor-pointer hover:shadow-[0_0_10px_rgba(255,255,255,0.2)] transition-all`} onClick={() => setSelectedTopic(topic)} data-id="element-2878">
              <div className="p-6 bg-black/90 rounded-lg h-full" data-id="element-2879">
                <h3 className="text-lg font-medium" data-id="element-2880">{topic}</h3>
              </div>
            </div>)}
        </div>
        {/* Add a button to show all subtopics at once */}
        <div className="mt-4 flex justify-center" data-id="element-2881">
          <button className={`px-6 py-3 rounded-lg bg-gradient-to-r ${gradient.from} ${gradient.to} text-white hover:opacity-90 transition-all flex items-center gap-2`} onClick={() => {
          // Use the unit title as the topic, but keep track of the unit for filtering
          setSelectedTopic(selectedUnit.title);
        }} data-id="element-2882">
            <BookOpen className="w-5 h-5" data-id="element-2883" />
            Tüm Alt Konuları Göster
          </button>
        </div>
      </div>;
  }
  // LEVEL 3: Question List (Soru Listesi) - For Solved Questions
  if (activeTab === 'solved' && selectedSubject && selectedTopic) {
    const gradient = getSubjectGradient(selectedSubject.id);
    // Filter questions based on search term, visibility, and archive status
    let filteredQuestions = questions.filter(q => q.subject === selectedSubject.title && q.topic === selectedTopic && (q.subject.toLowerCase().includes(searchTerm.toLowerCase()) || q.topic.toLowerCase().includes(searchTerm.toLowerCase()) || q.subtopic.toLowerCase().includes(searchTerm.toLowerCase())) && q.archived);
    // Apply visibility filter
    if (visibilityFilter !== 'everyone') {
      filteredQuestions = filteredQuestions.filter(q => {
        if (!q.visibility) return true;
        if (visibilityFilter === 'institution') return q.visibility === 'institution';
        if (visibilityFilter === 'private') return q.visibility === 'private';
        return true;
      });
    }
    // Sort filtered questions
    const sortedQuestions = sortQuestions(filteredQuestions);
    return <div className="space-y-6" data-id="element-2884">
        <div className="flex items-center gap-4" data-id="element-2885">
          <button onClick={() => {
          if (selectedSubject.id === 'fen' && selectedUnit) {
            handleBackFromUnit();
          } else {
            setSelectedTopic(null);
          }
        }} className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${gradient.from} ${gradient.to} text-white hover:opacity-90 transition-all`} data-id="element-2886">
            <ArrowLeft className="w-5 h-5" data-id="element-2887" />
            Geri
          </button>
          <h1 className="text-2xl font-bold" data-id="element-2888">
            {selectedSubject.title} - {selectedTopic}
          </h1>
        </div>
        {/* Filtering and Sorting Controls */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2889">
          <div className="relative bg-black/90 rounded-xl p-4" data-id="element-2890">
            <div className="flex items-center justify-between" data-id="element-2891">
              <div className="relative w-full" data-id="element-2892">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2893" />
                <input type="text" placeholder="Soru ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-white/5 text-white rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 ring-purple-500/20" data-id="element-2894" />
              </div>
            </div>
          </div>
        </div>
        {/* Questions List */}
        <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2895">
          <div className="relative p-6 bg-black/90 rounded-lg" data-id="element-2896">
            <div className="space-y-4" data-id="element-2897">
              {sortedQuestions.length > 0 ? sortedQuestions.map(question => <div key={question.id} className="p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-[0_0_10px_rgba(255,255,255,0.2)] transition-all" data-id="element-2898">
                    <div className="p-4 bg-black/90 rounded-lg" data-id="element-2899">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-2900">
                        <div className="flex-1" data-id="element-2901">
                          <div className="flex items-center gap-2 mb-1" data-id="element-2902">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${question.difficulty === 'Zor' ? 'bg-red-400/10 text-red-400' : question.difficulty === 'Orta' ? 'bg-yellow-400/10 text-yellow-400' : 'bg-green-400/10 text-green-400'}`} data-id="element-2903">
                              {question.difficulty}
                            </span>
                          </div>
                          <h3 className="font-medium text-white" data-id="element-2904">
                            {question.subtopic}
                          </h3>
                          <div className="mt-1 text-sm text-gray-400" data-id="element-2905">
                            {new Date(question.date).toLocaleDateString('tr-TR')}
                          </div>
                        </div>
                        <div className="flex items-center justify-center" data-id="element-2906">
                          <button onClick={() => {
                      setSelectedQuestion(question);
                      handleView(question.id);
                    }} className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:opacity-90 transition-colors w-full" data-id="element-2907">
                            <Eye className="w-5 h-5" data-id="element-2908" />
                            <span data-id="element-2909">Görüntüle</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>) : <div className="text-center py-12 text-gray-400" data-id="element-2910">
                  <div className="mb-4 flex justify-center" data-id="element-2911">
                    <Bookmark className="w-12 h-12 text-gray-600" data-id="element-2912" />
                  </div>
                  <h3 className="text-xl font-medium mb-2" data-id="element-2913">
                    Bu konuda arşivlenmiş soru bulunmuyor
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-2914">
                    Çözdüğünüz soruları arşive ekleyerek burada
                    görüntüleyebilirsiniz.
                  </p>
                </div>}
            </div>
          </div>
        </div>
        {/* Question Detail Modal */}
        {selectedQuestion && <ArchiveQuestionDetail question={selectedQuestion} onClose={() => setSelectedQuestion(null)} onToggleArchive={questionId => {
        handleToggleArchive(questionId);
        // If a question is unarchived, remove it from the selected question
        if (selectedQuestion.id === questionId) {
          setSelectedQuestion(null);
        }
      }} onSaveNotes={handleSaveNotes} subjectId={selectedSubject.id} data-id="element-2915" />}
        {/* Share Modal */}
        {showShareModal && questionToShare && <ShareQuestionModal questionId={questionToShare.id} questionTitle={questionToShare.subtopic} onClose={() => {
        setShowShareModal(false);
        setQuestionToShare(null);
      }} onShare={handleShareSubmit} data-id="element-2916" />}
      </div>;
  }
  // LEVEL 3: Question List (Soru Listesi) - For Forum Questions
  if (activeTab === 'forum' && selectedSubject && selectedTopic && !selectedForumQuestion) {
    const gradient = getSubjectGradient(selectedSubject.id);
    // Filter forum questions based on subject, topic, and search term
    const filteredForumQuestions = archivedForumQuestions.filter(q => {
      // For Fen Bilimleri, handle the topic hierarchy differently
      if (selectedSubject.id === 'fen' && selectedUnit) {
        // Check if the question's topic matches any of the subtopics in the selected unit
        return q.subject === selectedSubject.title && selectedUnit.topics.includes(q.topic) && (q.title.toLowerCase().includes(searchTerm.toLowerCase()) || q.subject.toLowerCase().includes(searchTerm.toLowerCase()) || q.topic.toLowerCase().includes(searchTerm.toLowerCase()) || q.content && q.content.toLowerCase().includes(searchTerm.toLowerCase()));
      } else if (selectedSubject.id === 'fen' && !selectedUnit) {
        // If we're showing all subtopics for a main topic in Fen Bilimleri
        // Find the unit that matches the selected topic
        const matchingUnit = (selectedSubject.subTopics as SubTopic[]).find(st => st.title === selectedTopic);
        if (matchingUnit) {
          // Show questions from any subtopic in this unit
          return q.subject === selectedSubject.title && matchingUnit.topics.includes(q.topic) && (q.title.toLowerCase().includes(searchTerm.toLowerCase()) || q.subject.toLowerCase().includes(searchTerm.toLowerCase()) || q.topic.toLowerCase().includes(searchTerm.toLowerCase()) || q.content && q.content.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        return false;
      } else {
        // For other subjects, continue with the original logic
        return q.subject === selectedSubject.title && q.topic === selectedTopic && (q.title.toLowerCase().includes(searchTerm.toLowerCase()) || q.subject.toLowerCase().includes(searchTerm.toLowerCase()) || q.topic.toLowerCase().includes(searchTerm.toLowerCase()) || q.content && q.content.toLowerCase().includes(searchTerm.toLowerCase()));
      }
    });
    // Sort filtered forum questions
    const sortedForumQuestions = (() => {
      switch (sortOption) {
        case 'newest':
          return [...filteredForumQuestions].sort((a, b) => new Date(b.archivedDate).getTime() - new Date(a.archivedDate).getTime());
        case 'oldest':
          return [...filteredForumQuestions].sort((a, b) => new Date(a.archivedDate).getTime() - new Date(b.archivedDate).getTime());
        case 'titleAZ':
          return [...filteredForumQuestions].sort((a, b) => a.title.localeCompare(b.title, 'tr'));
        case 'titleZA':
          return [...filteredForumQuestions].sort((a, b) => b.title.localeCompare(a.title, 'tr'));
        default:
          return [...filteredForumQuestions].sort((a, b) => new Date(b.archivedDate).getTime() - new Date(a.archivedDate).getTime());
      }
    })();
    return <div className="space-y-6" data-id="element-2917">
        <div className="flex items-center gap-4" data-id="element-2918">
          <button onClick={() => {
          if (selectedSubject.id === 'fen' && selectedUnit) {
            handleBackFromUnit();
          } else {
            setSelectedTopic(null);
          }
        }} className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${gradient.from} ${gradient.to} text-white hover:opacity-90 transition-all`} data-id="element-2919">
            <ArrowLeft className="w-5 h-5" data-id="element-2920" />
            Geri
          </button>
          <h1 className="text-2xl font-bold" data-id="element-2921">
            {selectedSubject.title} - {selectedTopic}
          </h1>
        </div>
        {/* Filtering and Sorting Controls */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2922">
          <div className="relative bg-black/90 rounded-xl p-4" data-id="element-2923">
            <div className="flex items-center justify-between" data-id="element-2924">
              <div className="relative w-full" data-id="element-2925">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2926" />
                <input type="text" placeholder="Soru ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-white/5 text-white rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 ring-purple-500/20" data-id="element-2927" />
              </div>
            </div>
          </div>
        </div>
        {/* Forum Questions List */}
        <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2928">
          <div className="relative p-6 bg-black/90 rounded-lg" data-id="element-2929">
            <div className="space-y-4" data-id="element-2930">
              {filteredForumQuestions.length > 0 ? filteredForumQuestions.map(question => {
              // Get color based on subject
              const subjectColor = (() => {
                switch (question.subject) {
                  case 'Matematik':
                    return 'from-blue-500 to-cyan-500';
                  case 'Türkçe':
                    return 'from-red-600 to-pink-600';
                  case 'Fen Bilimleri':
                    return 'from-emerald-600 to-teal-600';
                  case 'T.C. İnkılap Tarihi':
                    return 'from-amber-600 to-yellow-600';
                  case 'İngilizce':
                    return 'from-purple-500 to-violet-500';
                  case 'Din Kültürü':
                    return 'from-blue-900 to-blue-800';
                  default:
                    return 'from-purple-600 to-indigo-600';
                }
              })();
              return <div key={question.id} className={`relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${subjectColor} hover:shadow-[0_0_10px_rgba(255,255,255,0.2)] transition-all duration-300 cursor-pointer`} onClick={() => handleOpenForumQuestion(question.id)} data-id="element-2931">
                      <div className="relative p-4 bg-black/90 rounded-lg h-full" data-id="element-2932">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-2933">
                          <div className="flex-1 space-y-1" data-id="element-2934">
                            <div className="flex items-center gap-2" data-id="element-2935">
                              <MessageSquare className="w-4 h-4 text-gray-300" data-id="element-2936" />
                              <span className="text-white font-medium" data-id="element-2937">
                                {question.title}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2" data-id="element-2938">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${getCategoryColor(question.subject)}`} data-id="element-2939">
                                {question.subject}
                              </span>
                              <span className="px-2 py-0.5 rounded-full text-xs bg-purple-400/10 text-purple-400" data-id="element-2940">
                                {question.topic}
                              </span>
                            </div>
                            <div className="text-sm text-gray-400" data-id="element-2941">
                              Arşivlenme:{' '}
                              {new Date(question.archivedDate).toLocaleDateString('tr-TR')}
                            </div>
                          </div>
                          {/* Action buttons */}
                          <div className="flex items-center gap-3" data-id="element-2942">
                            {/* Open in forum button */}
                            <button onClick={e => {
                        e.stopPropagation();
                        handleOpenForumQuestion(question.id);
                      }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors" data-id="element-2943">
                              <ExternalLink className="w-4 h-4" data-id="element-2944" />
                              <span className="hidden sm:inline" data-id="element-2945">
                                Forumda Aç
                              </span>
                            </button>
                          </div>
                          {/* Status indicators */}
                          <div className="flex flex-wrap gap-2" data-id="element-2946">
                            {question.teacherApproved && <span className="px-2 py-0.5 rounded-full text-xs bg-green-400/10 text-green-400" data-id="element-2947">
                                Öğretmen Onaylı
                              </span>}
                            {question.bestAnswer && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-400/10 text-blue-400" data-id="element-2948">
                                Aradığım Cevap
                              </span>}
                            {question.mostLiked && <span className="px-2 py-0.5 rounded-full text-xs bg-purple-400/10 text-purple-400" data-id="element-2949">
                                En Beğenilen
                              </span>}
                          </div>
                        </div>
                      </div>
                    </div>;
            }) : <div className="text-center py-12 text-gray-400" data-id="element-2950">
                  <div className="mb-4 flex justify-center" data-id="element-2951">
                    <Bookmark className="w-12 h-12 text-gray-600" data-id="element-2952" />
                  </div>
                  <h3 className="text-xl font-medium mb-2" data-id="element-2953">
                    Bu konuda arşivlenmiş forum sorusu bulunmuyor
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-2954">
                    Forumda karşılaştığın zorlu veya önemli soruları 🔖 ikonuna
                    tıklayarak kişisel çalışma listen için buraya
                    ekleyebilirsin.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </div>;
  }
  // LEVEL 4: Show Forum Question Detail
  if (selectedForumQuestion) {
    return <ArchivedForumQuestionDetail question={selectedForumQuestion} onClose={() => setSelectedForumQuestion(null)} onSaveNotes={handleSaveForumQuestionNotes} data-id="element-2955" />;
  }
  {
    /* Note Modal */
  }
  {
    showNoteModal && selectedNoteQuestion && <NoteModal questionId={selectedNoteQuestion.id} initialNotes={selectedNoteQuestion.notes || ''} initialImages={'noteImages' in selectedNoteQuestion ? selectedNoteQuestion.noteImages : []} onClose={handleCloseNoteModal} onSave={handleSaveNotesFromModal} title="Bu Soru Hakkındaki Notların" data-id="element-2956" />;
  }
  {
    /* Toast container */
  }
  ;
  <ToastContainer data-id="element-2957" />;
  // Default return - this should not be reached normally
  return null;
}
const handleSelectSubject = (subject: any) => {
  setSelectedSubject(subject);
  // For Fen Bilimleri, we need to handle subTopics differently
  if (subject.id === 'fen' && subject.subTopics) {
    setTopics(subject.subTopics.map((subTopic: any) => ({
      id: subTopic.title,
      title: subTopic.title,
      count: subTopic.topics.length
    })));
  } else {
    // For other subjects, continue as normal
    setTopics(subject.topics.map((topic: string) => ({
      id: topic,
      title: topic,
      count: Math.floor(Math.random() * 10) + 1
    })));
  }
};